import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  Divider,
  Link,
} from "@mui/material";
import { Email } from "@mui/icons-material";
import { signInWithRedirect } from "aws-amplify/auth";
import { AtomicLogo } from "../../../assets/AtomicLogo";
import { useGoogleSignInLoading } from "../use_google_sign_in_loading";

interface LandingProps {
  onEmailSubmit: (email: string) => void;
  onGoogleSignIn: () => void;
}

// Our Pre Sign-up Lambda (iac env-features/nested/AuthenticationStack's
// PreSignUpLambda) prefixes its rejection with this marker - matching on it
// specifically, rather than Cognito's generic "PreSignUp failed" wrapper text,
// means an unrelated Lambda error (e.g. a transient AWS throttle) falls
// through to the generic message instead of this one.
const NOT_INVITED_MARKER = "NOT_INVITED";
// Worded to fit every NOT_INVITED reason the Lambda can raise (never
// invited, disabled, ambiguous match, a stray pre-this-feature account) -
// not just "unrecognized", which would be actively wrong for e.g. a
// disabled account the pool already knows about.
const SALES_EMAIL = "sales@atomic.supply";
// Exported so tests can assert the rendered alert's text against this exact
// constant, instead of duplicating the prose as a second literal in the
// test file (which would defeat the point of testing the anti-drift claim
// above).
export const UNINVITED_GOOGLE_ACCOUNT_MESSAGE =
  "We're unable to sign you in with this Google account. If your " +
  "organization already works with Atomic Supply, please contact your " +
  "administrator. If you're interested in learning how Atomic Supply can " +
  "help your team, we'd love to hear from you - reach out to us at " +
  `${SALES_EMAIL}.`;
const GENERIC_GOOGLE_SIGNIN_ERROR_MESSAGE =
  "Sign-in with Google didn't complete. Please try again.";

// Derived from UNINVITED_GOOGLE_ACCOUNT_MESSAGE (split on the email address)
// rather than a second copy of the prose, so the two can't drift: editing
// the string above is guaranteed to also update what renders here. The
// plain string is still what's used for the OAuth-callback-param matching
// logic and as the actual value stored in state; this is only swapped in at
// render time when that state equals UNINVITED_GOOGLE_ACCOUNT_MESSAGE.
//
// Mapped over every segment (rather than destructured into a fixed
// before/after pair) so this stays correct even if the prose above is ever
// edited to mention the address more than once, or not at all - a fixed
// 2-part destructure would silently drop the extra text in the first case.
const UNINVITED_GOOGLE_ACCOUNT_MESSAGE_SEGMENTS =
  UNINVITED_GOOGLE_ACCOUNT_MESSAGE.split(SALES_EMAIL);
const UNINVITED_GOOGLE_ACCOUNT_MESSAGE_JSX = (
  <>
    {UNINVITED_GOOGLE_ACCOUNT_MESSAGE_SEGMENTS.map((segment, index) => (
      <React.Fragment key={index}>
        {segment}
        {index < UNINVITED_GOOGLE_ACCOUNT_MESSAGE_SEGMENTS.length - 1 && (
          <Link href={`mailto:${SALES_EMAIL}`}>{SALES_EMAIL}</Link>
        )}
      </React.Fragment>
    ))}
  </>
);

// Captured at module scope - evaluated once, the moment this module first
// loads (i.e. on the actual page load that followed the Cognito redirect) -
// rather than in a per-component useState initializer. AmplifyAuthContent's
// checkAuth() sets isLoading=true while it (re-)verifies auth, which unmounts
// LandingComponent (swapped for AuthenticationLoading) and remounts it fresh
// afterwards, possibly more than once (the mount-time check, then again from
// the Hub listener); a useState initializer re-reading window.location.search
// on each of those remounts would see it already stripped by the first one
// and lose the message. A module-level constant can't re-run.
//
// Deliberately NOT "consume once": a one-time flag looks like the right fix
// for AuthenticationContainer also remounting this component for ordinary
// navigation (switching to password entry and back), but the OAuth-settling
// remounts above are *also* real remounts of the same component, and happen
// first - a flag consumed by an early, invisible one of those would make the
// message miss its own real debut. Reseeding from the constant on every mount
// means the message can reappear after an unrelated navigate-back; a minor
// cosmetic quirk, chosen over the message not reliably showing at all.
const INITIAL_OAUTH_CALLBACK_PARAMS =
  window.location.pathname === "/callback"
    ? new URLSearchParams(window.location.search)
    : new URLSearchParams();
const INITIAL_OAUTH_CALLBACK_ERROR = INITIAL_OAUTH_CALLBACK_PARAMS.has("error")
  ? (INITIAL_OAUTH_CALLBACK_PARAMS.get("error_description") || "").includes(
      NOT_INVITED_MARKER
    )
    ? UNINVITED_GOOGLE_ACCOUNT_MESSAGE
    : GENERIC_GOOGLE_SIGNIN_ERROR_MESSAGE
  : "";

export const LandingComponent = ({
  onEmailSubmit,
  onGoogleSignIn,
}: LandingProps) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  // Separate from `loading` (email form submission) - the Google button is
  // an independent action and shouldn't appear busy (or get disabled) while
  // the other one is in flight, or vice versa.
  const [googleSignInLoading, setGoogleSignInLoading] =
    useGoogleSignInLoading();
  const [error, setError] = useState("");
  // Kept separate from `error` (email validation / Google-sign-in-call
  // failures): `error` wins when both are set (a fresh validation failure is
  // more actionable than a stale OAuth message), and each new form action
  // explicitly clears this so it doesn't linger across unrelated attempts.
  // This only covers the case where the user has no existing session
  // (LandingComponent doesn't mount at all otherwise) - an already-
  // authenticated user rejected while linking a second Google account
  // currently gets no feedback here; known gap, not handled.
  const [oauthCallbackError, setOauthCallbackError] = useState(
    INITIAL_OAUTH_CALLBACK_ERROR
  );

  useEffect(() => {
    if (!INITIAL_OAUTH_CALLBACK_ERROR) return;
    // Drop only the error params so a refresh doesn't keep re-showing this,
    // while preserving anything else that survived the OAuth round trip.
    const params = new URLSearchParams(window.location.search);
    params.delete("error");
    params.delete("error_description");
    const query = params.toString();
    window.history.replaceState(
      {},
      "",
      `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`
    );
  }, []);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setOauthCallbackError("");

    if (!email.trim()) {
      setError("Please enter your email address");
      setLoading(false);
      return;
    }

    if (!validateEmail(email)) {
      setError("Please enter a valid email address");
      setLoading(false);
      return;
    }

    try {
      onEmailSubmit(email.trim());
    } catch (err: any) {
      console.error("Email submission error:", err);
      setError("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError("");
    setOauthCallbackError("");
    setGoogleSignInLoading(true);
    try {
      await signInWithRedirect({ provider: "Google" });
      onGoogleSignIn();
    } catch (error) {
      console.error("Google sign in error:", error);
      setError("Failed to sign in with Google. Please try again.");
      setGoogleSignInLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        background: "#fafafa",
        padding: 2,
      }}
    >
      <Card
        sx={{
          maxWidth: 400,
          width: "100%",
          borderRadius: 2,
          border: "1px solid #e0e0e0",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
        }}
      >
        <CardContent sx={{ padding: 4 }}>
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <Box sx={{ mb: 2 }}>
              <AtomicLogo width={64} height={64} />
            </Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Welcome to Atomic
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Get started with your account
            </Typography>
          </Box>

          {(error || oauthCallbackError) && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error
                ? error
                : oauthCallbackError === UNINVITED_GOOGLE_ACCOUNT_MESSAGE
                  ? UNINVITED_GOOGLE_ACCOUNT_MESSAGE_JSX
                  : oauthCallbackError}
            </Alert>
          )}

          {/* Google Sign In Button */}
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleGoogleSignIn}
            disabled={googleSignInLoading}
            sx={{
              mb: 3,
              py: 1.5,
              backgroundColor: "#3877F7",
              "&:hover": {
                backgroundColor: "#2d5fd6",
              },
            }}
            startIcon={
              googleSignInLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24">
                  <path
                    fill="#ffffff"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#ffffff"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#ffffff"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#ffffff"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
              )
            }
          >
            {googleSignInLoading ? "Redirecting..." : "Continue with Google"}
          </Button>

          <Divider sx={{ my: 2 }}>
            <Typography variant="body2" color="text.secondary">
              or
            </Typography>
          </Divider>

          {/* Email Input Form */}
          <Box component="form" onSubmit={handleEmailSubmit}>
            <TextField
              fullWidth
              label="Enter your email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              margin="normal"
              placeholder="you@example.com"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />

            <Button
              type="submit"
              fullWidth
              variant="outlined"
              size="large"
              disabled={loading || !email.trim()}
              startIcon={
                loading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : undefined
              }
              sx={{
                py: 1.5,
                borderColor: "#3877F7",
                color: "#3877F7",
                "&:hover": {
                  borderColor: "#2d5fd6",
                  backgroundColor: "rgba(56, 119, 247, 0.04)",
                },
              }}
            >
              {loading ? "Checking..." : "Continue"}
            </Button>
          </Box>

          <Box sx={{ textAlign: "center", mt: 3 }}>
            <Typography variant="body2" color="text.secondary">
              By continuing, you agree to our Terms of Service and Privacy
              Policy
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
